import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { getCompanySuggestions } from '../api';

/**
 * Multi-competitor filter as removable chips + typeahead against snapshot company names.
 * Each chip is one substring sent to the competitive-intelligence API (same as comma-separated input).
 */
export default function CompetitorChipPicker({
  value,
  onChange,
  excludeCompanyName,
  inputId
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const wrapRef = useRef(null);
  const excludeNorm = (excludeCompanyName || '').trim().toLowerCase();
  useEffect(() => {
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getCompanySuggestions(q);
        let names = res.data?.data || [];
        if (excludeNorm) {
          names = names.filter(n => (n || '').trim().toLowerCase() !== excludeNorm);
        }
        if (!cancelled) setSuggestions(names);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, excludeNorm]);
  const addChip = raw => {
    const s = raw.trim();
    if (!s) return;
    if (value.some(v => v.toLowerCase() === s.toLowerCase())) {
      setQuery('');
      setOpen(false);
      return;
    }
    onChange([...value, s]);
    setQuery('');
    setSuggestions([]);
    setOpen(false);
  };
  const removeChip = idx => {
    onChange(value.filter((_, i) => i !== idx));
  };
  const showPanel = open && query.trim().length >= 2;
  return <div ref={wrapRef} className="chip-picker">
      <div className="chip-picker__control">
        {value.map((tag, i) => <span key={`${tag}-${i}`} className="chip-picker__chip">
            <span className="chip-picker__chip-label">{tag}</span>
            <button type="button" aria-label={`Remove ${tag}`} className="btn-clear chip-picker__chip-remove" onClick={() => removeChip(i)}>
              <X size={14} strokeWidth={2.25} />
            </button>
          </span>)}
        <input id={inputId} type="text" value={query} onChange={e => {
        setQuery(e.target.value);
        setOpen(true);
      }} onFocus={() => setOpen(true)} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addChip(query);
        }
        if (e.key === 'Backspace' && !query && value.length) {
          removeChip(value.length - 1);
        }
      }} placeholder={value.length ? 'Type to search carriers, Enter adds custom text…' : 'Search carriers or type a filter, Enter to add'} className="chip-picker__input" />
      </div>
      {showPanel ? <div className="chip-picker__menu">
          {loading ? <div className="chip-picker__menu-hint">Searching…</div> : suggestions.length === 0 ? <button type="button" onClick={() => addChip(query)} className="chip-picker__menu-add">
              Add filter &quot;{query.trim()}&quot; (substring match)
            </button> : suggestions.map((name, idx) => <button key={name} type="button" onClick={() => addChip(name)} style={{
        borderBottom: idx < suggestions.length - 1 ? '1px solid var(--border)' : 'none'
      }} className="chip-picker__menu-option">
                {name}
              </button>)}
        </div> : null}
    </div>;
}